import React from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { Redirect, Route, Switch } from 'react-router'
import { NotFound, Unauthorized } from 'secondstep-components'
import { VIEW_OPTION_PATHS } from 'utils/adminExperienceHelpers'
import { TOGGLE_VIEW } from 'store/userSessionManager/constants'
import withApiError, {
  ERROR_TYPE_LMS,
  ERROR_TYPE_PROFILE,
} from 'layers/errorHandling/apiError'
import { operations as userSessionOperations } from 'store/userSessionManager'
import AdminDashboard from 'pages/AdminDashboard'
import AdminSubPages from 'pages/AdminSubPages'
import UserProgressRouter from 'layers/routers/UserProgressRouter'
import InterventionsRouter from 'layers/routers/InterventionsRouter'
import PagesContainer from '../../Pages'
import LearnErrorBoundary from 'components/LearnErrorBoundary'
import { BPU, CPU, HS, K8, SELA, SETUP } from './constants'
import { getStaticRoutes } from './data'
import AdminSetup from 'components/AdminSetup'

export const AdminRouter = ({
  hsToggleView,
  isFirstTimeSetupAdmin,
  k8ToggleView,
  match,
  recentLeaderDashboardUrl,
  userClaims,
}) => {
  const { path } = match
  const {
    hasBpuAdminClaim = false,
    hasCpuAdminClaim = false,
    hasElemAdminClaim = false,
    hasHighschoolAdminClaim = false,
    hasMsAdminClaim = false,
    hasSelaAdminClaim = false,
  } = userClaims || {}

  let k8ViewOption = VIEW_OPTION_PATHS.PROGRAM_ADOPTION
  let hsViewOption = VIEW_OPTION_PATHS.PROGRAM_ADOPTION

  if (!path.includes('user-progress')) {
    if (!recentLeaderDashboardUrl) {
      // No recent URL - always use generic paths, let AdminDashboard handle view selection
      k8ViewOption = null
      hsViewOption = null
    } else {
      if (k8ToggleView) {
        if (k8ToggleView === TOGGLE_VIEW.MONITOR) {
          k8ViewOption = VIEW_OPTION_PATHS.MONITOR
        } else if (k8ToggleView === TOGGLE_VIEW.PREPARE) {
          k8ViewOption = VIEW_OPTION_PATHS.PREPARE
        } else if (k8ToggleView === TOGGLE_VIEW.PROGRAM_ADOPTION) {
          k8ViewOption = VIEW_OPTION_PATHS.PROGRAM_ADOPTION
        } else if (k8ToggleView === TOGGLE_VIEW.INTERVENTIONS) {
          k8ViewOption = VIEW_OPTION_PATHS.INTERVENTIONS
        }
      }
      if (hsToggleView) {
        if (hsToggleView === TOGGLE_VIEW.MONITOR) {
          hsViewOption = VIEW_OPTION_PATHS.MONITOR
        } else if (hsToggleView === TOGGLE_VIEW.PREPARE) {
          hsViewOption = VIEW_OPTION_PATHS.PREPARE
        } else {
          hsViewOption = VIEW_OPTION_PATHS.PROGRAM_ADOPTION
        }
      }
    }
  }

  const hasAccessRoute = {
    k8: {
      hasAccess: hasElemAdminClaim || hasMsAdminClaim,
      validName: K8,
      // Use generic path when no specific view, specific path when we have one
      path: k8ViewOption ? `${K8}/${k8ViewOption}` : K8,
    },
    hs: {
      hasAccess: hasHighschoolAdminClaim,
      validName: HS,
      // Use generic path when no specific view, specific path when we have one
      path: hsViewOption ? `${HS}/${hsViewOption}` : HS,
    },
    bpu: {
      hasAccess: hasBpuAdminClaim,
      validName: BPU,
      path: BPU,
    },
    cpu: {
      hasAccess: hasCpuAdminClaim,
      validName: CPU,
      path: CPU,
    },
    sela: {
      hasAccess: hasSelaAdminClaim,
      validName: SELA,
      path: SELA,
    },
  }

  const getDefaultAdminRoute = () => {
    const checkAccessAndReturnRoute = () => {
      for (const route in hasAccessRoute) {
        const program = hasAccessRoute[route]
        if (program.hasAccess) {
          const qualifiedPath = path.includes('program')
            ? program.path
            : `program/${program.path}`
          return qualifiedPath
        }
      }
    }

    if (isFirstTimeSetupAdmin) {
      return `${SETUP}`
    } else if (!recentLeaderDashboardUrl) {
      return checkAccessAndReturnRoute()
    } else {
      // check access to the program in the persisted url and if no access clear the persisted url
      const url = recentLeaderDashboardUrl.split('/')
      const programTextIndex = url.indexOf('program')
      const parsedProgram = url[programTextIndex + 1]
      const programToAccess = hasAccessRoute[parsedProgram]

      if (!programToAccess.hasAccess) {
        return checkAccessAndReturnRoute()
      } else {
        const formattedUrl = url.slice(programTextIndex).join('/') // Gaurentees that the url is in the right format of program/:programName/:viewOption
        return formattedUrl
      }
    }
  }

  const getProtectedAdminRoute = routerProps => {
    const { programName = '' } = routerProps?.match?.params
    const program = programName.toLowerCase()
    const { hasAccess = false, validName = '' } = hasAccessRoute[program] || {}
    const isUserProgressRoute = routerProps.location.pathname.includes(
      'user-progress',
    )
    const isSmallGroupLessonCompletionRoute = routerProps.location.pathname.includes(
      'small-group-lesson-completion',
    )

    if (hasAccess && isUserProgressRoute) {
      if (validName === HS) {
        return <UserProgressRouter {...routerProps} />
      }
    } else if (hasAccess && isSmallGroupLessonCompletionRoute) {
      if (validName === K8) {
        return <InterventionsRouter {...routerProps} />
      }
    } else if (isFirstTimeSetupAdmin && validName === '' && path === '/admin') {
      return <AdminSetup {...routerProps} />
    } else if (hasAccess && program === validName) {
      return <AdminDashboard {...routerProps} />
    } else if (!hasAccess && program === validName) {
      return <Unauthorized />
    } else {
      return <NotFound {...routerProps} />
    }
  }

  const adminDefaultRoute =
    path.includes('admin') && !path.includes('user-progress')
      ? getDefaultAdminRoute()
      : ''
  return (
    <LearnErrorBoundary>
      <Switch>
        <Route
          component={AdminSubPages}
          exact
          path={`${path}/hs-implementation-guide`}
        />
        <Route
          component={AdminSubPages}
          exact
          path={`${path}/implementation-guide`}
        />
        <Route component={PagesContainer} path={getStaticRoutes(path)} />
        <Route exact path={`/admin/setup`} render={getProtectedAdminRoute} />
        <Route
          path={'/admin/program/:programName/user-progress'}
          render={getProtectedAdminRoute}
        />
        <Redirect
          exact
          path={`/admin/program/k8/tier_2`}
          to={`/admin/program/k8/interventions`}
        />
        <Route
          path={'/admin/program/:programName/small-group-lesson-completion'}
          render={getProtectedAdminRoute}
        />
        <Route
          exact
          path={'/admin/program/:programName/:viewOption'}
          render={getProtectedAdminRoute}
        />
        <Route
          exact
          path={'/admin/program/:programName'}
          render={getProtectedAdminRoute}
        />
        <Redirect
          exact
          from={'/admin/program'}
          to={`/admin/program/${adminDefaultRoute}`}
        />
        <Redirect exact from={`/admin`} to={`/admin/${adminDefaultRoute}`} />
        <Route component={NotFound} />
      </Switch>
    </LearnErrorBoundary>
  )
}

AdminRouter.propTypes = {
  hsToggleView: PropTypes.oneOf([
    TOGGLE_VIEW.PREPARE,
    TOGGLE_VIEW.LAUNCH,
    TOGGLE_VIEW.MONITOR,
  ]),
  isFirstTimeSetupAdmin: PropTypes.bool,
  k8ToggleView: PropTypes.oneOf([
    TOGGLE_VIEW.PREPARE,
    TOGGLE_VIEW.LAUNCH,
    TOGGLE_VIEW.MONITOR,
  ]),
  location: PropTypes.object,
  match: PropTypes.object,
  recentLeaderDashboardUrl: PropTypes.string,
  roles: PropTypes.array,
  setLeaderDashboardRecentUrl: PropTypes.func,
  userClaims: PropTypes.object,
}

const mapStateToProps = ({
  userAccessManager,
  userProfileManager,
  userSessionManager,
}) => {
  return {
    roles: userProfileManager?.profile?.roles ?? [],
    userClaims: userAccessManager?.claims,
    recentLeaderDashboardUrl:
      userSessionManager?.adminDashboard?.recentLeaderDashboardUrl,
  }
}
const mapDispatchToProps = {
  setLeaderDashboardRecentUrl:
    userSessionOperations.setLeaderDashboardRecentUrl,
}

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(withApiError(AdminRouter, [ERROR_TYPE_PROFILE, ERROR_TYPE_LMS]))
